import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { API_URL, popupTimer } from "../../consts";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";

const ORG_PERMISSIONS = {
  view_only: "View",
  add_items: "Add items",
  manage_collections: "Manage",
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 440,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 5,
  p: 4,
};

// Helper function to recursively flatten nested spaces
// Adjusted to use 'childrens' from your response JSON
const flattenSpaces = (spaceList = []) => {
  let result = [];
  for (const space of spaceList) {
    result.push(space);
    if (space.childrens && space.childrens.length > 0) {
      result = result.concat(flattenSpaces(space.childrens));
    }
  }
  return result;
};

const CreateSpace = ({ open, handleClose, onSpaceAdded, isSubSpaceMode, parentSpaceId, organizationId, organizationName }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    space_name: "",
    description: "",
    keywords: [],
    organization_visibility: "everyone",
    organization_access: [],
  });
  const [orgMembers, setOrgMembers] = useState([]);
  const [orgMembersLoading, setOrgMembersLoading] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [error, setError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -------------------------------------------------------------------------
  // State for the list of spaces (flattened), loading indicator, and parent
  // -------------------------------------------------------------------------
  const [spaces, setSpaces] = useState([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [parentSpace, setParentSpace] = useState(null);

  // -------------------------------------------------------------------------
  // Fetch existing spaces on mount
  // -------------------------------------------------------------------------
  const getSpaces = async (spaceId = null) => {
    setSpacesLoading(true);
    try {
      const url = spaceId 
        ? `${API_URL}/spaces?space_id=${spaceId}`
        : `${API_URL}/spaces`;
      const response = await axios.get(url);
      const nestedSpaces = response.data.spaces || [];
      
      // Get current space ID from URL
      const currentSpaceId = location.pathname.split('/spaces/')[1];
      
      // If we're fetching a specific space (parent space)
      if (spaceId) {
        // Search for the current space in the response
        const currentSpace = nestedSpaces.find(space => space.id === currentSpaceId);
        if (currentSpace) {
          setSpaces([currentSpace]);
          setParentSpace(currentSpace);
        } else {
          // If current space not found, use the first space
          setSpaces([nestedSpaces[0]]);
          setParentSpace(nestedSpaces[0]);
        }
      } else {
        // Otherwise flatten all spaces
        const flatSpaces = flattenSpaces(nestedSpaces);
        setSpaces(flatSpaces);
        // Find and set the current space as parent
        if (currentSpaceId) {
          const currentSpace = flatSpaces.find((s) => s.id === currentSpaceId);
          if (currentSpace) {
            setParentSpace(currentSpace);
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSpacesLoading(false);
    }
  };

  useEffect(() => {
    if (isSubSpaceMode && parentSpaceId) {
      // If we have a parent space ID, fetch that space
      getSpaces(parentSpaceId);
    } else {
      // For top-level spaces, just fetch all spaces without ID
      getSpaces();
    }
  }, [parentSpaceId, isSubSpaceMode]);

  // Fetch org members when creating organization collection
  useEffect(() => {
    if (organizationId && open) {
      setOrgMembersLoading(true);
      axios
        .get(`${API_URL}/tenants/organizations/${organizationId}/members/for-selection`, { withCredentials: true })
        .then((res) => setOrgMembers(res.data.members || []))
        .catch(() => setOrgMembers([]))
        .finally(() => setOrgMembersLoading(false));
    } else {
      setOrgMembers([]);
    }
  }, [organizationId, open]);

  // -------------------------------------------------------------------------
  // After spaces are fetched, set the default parent if in subspace mode
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!spacesLoading && spaces.length > 0 && isSubSpaceMode) {
      // Get current space ID from URL
      const currentSpaceId = location.pathname.split('/spaces/')[1];
      if (currentSpaceId) {
        const currentSpace = spaces.find((s) => s.id === currentSpaceId);
        if (currentSpace) {
          setParentSpace(currentSpace);
        }
      }
    }
  }, [spacesLoading, spaces, isSubSpaceMode, location.pathname]);

  // -------------------------------------------------------------------------
  // Handle parent space selection
  // -------------------------------------------------------------------------
  const handleParentSpaceChange = async (event, newValue) => {
    if (newValue && newValue.has_children) {
      // If the selected space has children, fetch them
      await getSpaces(newValue.id);
    }
    setParentSpace(newValue || null);
  };

  // -------------------------------------------------------------------------
  // Handle text field changes
  // -------------------------------------------------------------------------
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "space_name") {
      const isNameTooLong = value.length > 64;
      setNameError(isNameTooLong);
      if (!isNameTooLong) {
        setForm({ ...form, [name]: value });
      }
    } else if (name === "description") {
      const isDescriptionTooLong = value.length > 128;
      setDescriptionError(isDescriptionTooLong);
      if (!isDescriptionTooLong) {
        setForm({ ...form, [name]: value });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
    setSubmit(false);
  };

  // -------------------------------------------------------------------------
  // Submitting the form (including optional parent_space_id)
  // -------------------------------------------------------------------------
  const handleFormSubmit = () => {
    setSubmit(true);
    const isValid =
      form.space_name &&
      form.space_name.length <= 64 &&
      (!form.description || form.description.length <= 256);

    if (isValid) {
      setIsSubmitting(true);
      let url = API_URL + "/spaces";

      let payload = {
        ...form,
      };

      // Organization collection: send organization_id and access with permissions
      if (organizationId) {
        payload.organization_id = organizationId;
        payload.organization_visibility = form.organization_visibility || "everyone";
        payload.organization_access = (form.organization_access || []).map((entry) => ({
          email: typeof entry === "object" && entry?.email ? entry.email : entry,
          permissions: Array.isArray(entry?.permissions) ? entry.permissions : ["view_only"],
        }));
      }

      // Get current space ID from URL
      const currentSpaceId = location.pathname.split('/spaces/')[1];
      
      // If we're in subspace mode, use the current space as parent (not used when organizationId is set)
      if (!organizationId) {
        if (isSubSpaceMode && currentSpaceId) {
          payload.parent_space_id = currentSpaceId;
        } else if (parentSpace) {
          payload.parent_space_id = parentSpace.id;
        }
      }

      axios
        .post(url, payload)
        .then((response) => {
          setError(false);
          setIsSubmitting(false);
          // Pass the new space data back to the parent
          onSpaceAdded(response.data.space);
          setSubmit(false);
          setForm({
            space_name: "",
            description: "",
            keywords: [],
            organization_visibility: "everyone",
            organization_access: [],
          });
          setParentSpace(null); // reset selection
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "New Space Added Successfully",
              type: "success",
            },
          });
          setTimeout(() => {
            dispatch({
              type: "SET_POPUP",
              payload: {
                text: "",
                type: "",
              },
            });
          }, popupTimer);
        })
        .catch((err) => {
          setIsSubmitting(false);
          if (err?.response?.status === 401) {
            dispatch({ type: "SET_UNAUTHORIZED" });
            return;
          }
          const errorMessage =
            err?.response?.data?.error ||
            "Failed to add collection. Please try again.";
          dispatch({
            type: "SET_POPUP",
            payload: { text: errorMessage, type: "error" },
          });
          setTimeout(() => {
            dispatch({
              type: "SET_POPUP",
              payload: { text: "", type: "" },
            });
          }, popupTimer);
        });
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      closeAfterTransition
    >
      <Fade in={open}>
        <Box sx={style} className="signup-modal">
          <Typography id="transition-modal-title" variant="h6" component="h2">
            <CloseIcon
              className="cross-icon"
              onClick={() => {
                handleClose();
                setSubmit(false);
                setError(false);
                setNameError(false);
                setDescriptionError(false);
                setIsSubmitting(false);
                setForm({
                  space_name: "",
                  description: "",
                  keywords: [],
                  organization_visibility: "everyone",
                  organization_access: [],
                });
                setParentSpace(null);
              }}
            />
            <div className="card-heading mb-81"></div>
          </Typography>

          <Box id="transition-modal-description">
            <Typography
              id="transition-modal-title"
              variant="h2"
              component="h2"
              sx={{ fontSize: "20px", textAlign: "center", fontWeight: "700" }}
            >
              {organizationId
                ? (organizationName ? `Create collection for ${organizationName}` : "Create collection for your organization")
                : "Add your new collection"}
            </Typography>

            <TextField
              name="space_name"
              hiddenLabel
              type="text"
              fullWidth
              className="loginTextfields"
              id="standard-basic"
              onChange={handleFormChange}
              value={form.space_name}
              label="Space Name"
              variant="standard"
              error={(submit && form.space_name === "") || nameError}
              helperText={
                submit && form.space_name === ""
                  ? "Field is Required"
                  : nameError
                  ? "Space name cannot exceed 64 characters"
                  : ""
              }
              sx={{
                marginTop: "20px",
                borderColor: nameError ? "red" : "",
                "& .MuiInput-underline:before": {
                  borderBottomColor: nameError ? "red" : "",
                },
              }}
            />

            <TextField
              name="description"
              hiddenLabel
              type="text"
              fullWidth
              className="loginTextfields"
              id="standard-basic"
              onChange={handleFormChange}
              value={form.description}
              label="Description"
              variant="standard"
              error={descriptionError}
              helperText={
                descriptionError
                  ? "Space description cannot exceed 128 characters"
                  : ""
              }
              sx={{
                marginTop: "20px",
                borderColor: descriptionError ? "red" : "",
                "& .MuiInput-underline:before": {
                  borderBottomColor: descriptionError ? "red" : "",
                },
              }}
            />

            {/* Organization collection access - who can see, add, or manage */}
            {organizationId && (
              <>
                <FormControl component="fieldset" sx={{ marginTop: "20px", width: "100%" }}>
                  <FormLabel component="legend" sx={{ fontSize: "14px" }}>
                    Who can access this collection
                  </FormLabel>
                  <RadioGroup
                    row
                    value={form.organization_visibility || "everyone"}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        organization_visibility: e.target.value,
                        organization_access: e.target.value === "everyone" ? [] : form.organization_access,
                      })
                    }
                  >
                    <FormControlLabel value="everyone" control={<Radio />} label="Everyone (view only)" />
                    <FormControlLabel value="specific" control={<Radio />} label="Specific users" />
                  </RadioGroup>
                </FormControl>
                {form.organization_visibility === "everyone" && (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                    Optionally add members with add or manage permissions:
                  </Typography>
                )}
                {form.organization_visibility === "specific" && (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                    Only these members can access. Set each member&apos;s permission (View / Add / Manage).
                  </Typography>
                )}
                <Autocomplete
                  multiple
                  id="org-access-members"
                  options={orgMembers}
                  loading={orgMembersLoading}
                  value={
                    (form.organization_access || []).map((entry) => {
                      const email = typeof entry === "object" ? entry?.email : entry;
                      return orgMembers.find((m) => m.email === email) || { email };
                    })
                  }
                  onChange={(e, newVal) => {
                    const existing = form.organization_access || [];
                    const byEmail = Object.fromEntries(existing.map((x) => [typeof x === "object" ? x?.email : x, x]));
                    const access = newVal.map((m) => {
                      const email = m?.email || m;
                      const prev = byEmail[email];
                      const perms = typeof prev === "object" && Array.isArray(prev?.permissions) ? prev.permissions : ["view_only"];
                      return { email, permissions: perms };
                    });
                    setForm({ ...form, organization_access: access });
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option?.email
                      ? `${option.email}${option.first_name || option.last_name ? ` (${[option.first_name, option.last_name].filter(Boolean).join(" ")})` : ""}`
                      : String(option)
                  }
                  isOptionEqualToValue={(opt, val) =>
                    (typeof opt === "object" ? opt?.email : opt) === (typeof val === "object" ? val?.email : val)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={form.organization_visibility === "specific" ? "Add members (organization only)" : "Add members with permissions"}
                      placeholder="Select organization members"
                      sx={{ marginTop: "8px" }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {orgMembersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const email = option?.email || option;
                      const entry = (form.organization_access || []).find((e) => (typeof e === "object" ? e?.email : e) === email);
                      const perms = typeof entry === "object" && entry?.permissions ? entry.permissions : ["view_only"];
                      const permValue = perms.includes("manage_collections") ? "manage_collections" : perms.includes("add_items") ? "add_items" : "view_only";
                      return (
                        <Box component="span" key={email} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mr: 0.5, mb: 0.5 }}>
                          <Chip
                            label={orgMembers.find((m) => m.email === email) ? `${email.split("@")[0]}` : email}
                            size="small"
                            {...getTagProps({ index })}
                          />
                          <Select
                            size="small"
                            value={permValue}
                            onChange={(ev) => {
                              ev.stopPropagation();
                              const perm = ev.target.value;
                              const newPerms = perm === "manage_collections" ? ["view_only", "add_items", "manage_collections"] : perm === "add_items" ? ["view_only", "add_items"] : ["view_only"];
                              const updated = (form.organization_access || []).map((e) =>
                                (typeof e === "object" ? e?.email : e) === email ? { ...(typeof e === "object" ? e : {}), email, permissions: newPerms } : e
                              );
                              setForm({ ...form, organization_access: updated });
                            }}
                            onClick={(ev) => ev.stopPropagation()}
                            onMouseDown={(ev) => ev.stopPropagation()}
                            sx={{ minWidth: 90, height: 24, fontSize: "0.75rem" }}
                          >
                            <MenuItem value="view_only">{ORG_PERMISSIONS.view_only}</MenuItem>
                            <MenuItem value="add_items">{ORG_PERMISSIONS.add_items}</MenuItem>
                            <MenuItem value="manage_collections">{ORG_PERMISSIONS.manage_collections}</MenuItem>
                          </Select>
                        </Box>
                      );
                    })
                  }
                />
              </>
            )}

            {/* 
              Autocomplete for selecting a Parent Space (optional).
              Hidden when creating an organization collection.
            */}
            {!organizationId && (
              <Autocomplete
                id="parent-spaces-autocomplete"
                options={spaces}
                loading={spacesLoading}
                value={parentSpace}
                onChange={handleParentSpaceChange}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Parent Space (Optional)"
                    sx={{ marginTop: "20px" }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {spacesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <Autocomplete
              multiple
              freeSolo
              id="tags-outlined"
              options={[]}
              defaultValue={[]}
              value={form.keywords}
              clearOnBlur={true}
              onChange={(e, newval) => {
                setForm({ ...form, keywords: newval });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder="Add Keywords"
                  sx={{ marginTop: "20px" }}
                  onChange={() => setSubmit(false)}
                  onBlur={(e) => {
                    if (e.target.value !== "") {
                      setForm({
                        ...form,
                        keywords: form.keywords.concat(e.target.value),
                      });
                    }
                  }}
                />
              )}
            />

            <Typography paragraph sx={{ fontSize: "12px" }} className="textMuted">
              Each Tag must contain at least two characters.
            </Typography>

            <Grid container sx={{ margin: "0 auto" }}>
              <Grid item sx={{ margin: "0 auto" }}>
                <Button
                  className="connectButton"
                  onClick={handleFormSubmit}
                  disabled={
                    form.keywords.some((tag) => tag.length < 2) ||
                    nameError ||
                    descriptionError ||
                    isSubmitting
                  }
                  variant="contained"
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isSubmitting ? "Adding..." : "Add collection"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateSpace;
